import React, { useEffect, useState } from "react";
import './App.css';

const APP_TITLE = "BitcoinAgree V1.17.0";
const THEME_KEY = "bitcoinagree_theme";
const DEFAULT_THEME_CONFIG = {
  version: "fallback",
  layout: {
    shellPadding: "28px 16px",
    maxWidth: 900,
    cardRadius: 8,
    cardPadding: 18,
    cardMarginBottom: 16,
    softCardPadding: 14,
    sectionGap: 16,
    gridGap: 12,
    toolMinWidth: 220,
    apiMinWidth: 150,
    buttonRadius: 6
  },
  themes: {
    light: {
      bg: "#f6f8fb",
      surface: "#ffffff",
      surfaceSoft: "#f8fafc",
      text: "#17202a",
      muted: "#52616f",
      border: "#d8e0ea",
      bitcoin: "#f7931a",
      bitcoinStrong: "#b96000",
      focus: "#2563eb",
      success: "#17803d",
      danger: "#b42318"
    },
    dark: {
      bg: "#101418",
      surface: "#171d24",
      surfaceSoft: "#202832",
      text: "#edf2f7",
      muted: "#a8b3c2",
      border: "#334152",
      bitcoin: "#f7a531",
      bitcoinStrong: "#ffc46b",
      focus: "#7aa7ff",
      success: "#5fd18b",
      danger: "#ff8a80"
    }
  }
};

function getCookie(name) {
  const parts = document.cookie.split(";").map(function(part) { return part.trim(); });
  for (let i = 0; i < parts.length; i++) {
    if (parts[i].indexOf(name + "=") === 0) {
      return decodeURIComponent(parts[i].substring(name.length + 1));
    }
  }
  return "";
}

function getCookieDomain() {
  const host = window.location.hostname;
  if (host === "bitcoinagree.com" || host.endsWith(".bitcoinagree.com")) return ".bitcoinagree.com";
  if (host === "test.local" || host.endsWith(".test.local")) return ".test.local";
  return "";
}

function getStoredThemeMode() {
  const cookieMode = getCookie(THEME_KEY);
  if (cookieMode === "auto" || cookieMode === "light" || cookieMode === "dark") return cookieMode;
  try {
    const localMode = window.localStorage.getItem(THEME_KEY);
    if (localMode === "auto" || localMode === "light" || localMode === "dark") return localMode;
  } catch (e) {}
  return "auto";
}

function resolveTheme(mode) {
  if (mode === "light" || mode === "dark") return mode;
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  } catch (e) {
    return "light";
  }
}

function saveThemeMode(mode) {
  try { window.localStorage.setItem(THEME_KEY, mode); } catch (e) {}
  const domain = getCookieDomain();
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  const domainPart = domain ? "; Domain=" + domain : "";
  document.cookie = THEME_KEY + "=" + encodeURIComponent(mode) + "; Max-Age=31536000; Path=/; SameSite=Lax" + domainPart + secure;
}

function getThemeTokens(theme, config) {
  const source = config && config.themes ? config : DEFAULT_THEME_CONFIG;
  return source.themes[theme] || source.themes.light || DEFAULT_THEME_CONFIG.themes.light;
}

function getApiUrl(service) {
  const host = window.location.hostname;
  let domain, protocol;
  if (
    host === "bitcoinagree.com" || host.endsWith("bitcoinagree.com")
  ) {
    domain = "bitcoinagree.com";
    protocol = "https";
  } else if (
    host === "localhost" || host.endsWith(".test") || host.endsWith(".local")
  ) {
    // local dev
    domain = "test.local";
    protocol = "http";
  } else {
    // fallback
    domain = host;
    protocol = window.location.protocol.replace(":", "");
  }
  const urls = {
    time: `${protocol}://time.${domain}/`,
    joke: `${protocol}://joke.${domain}/`,
    bitcoinApi: `${protocol}://bitcoin.api.${domain}/v1/`,
    bitcoinPrice: `${protocol}://price.${domain}/`,
  bitcoinPriceDate: `${protocol}://price-date.${domain}/`,
    theme: `${protocol}://theme.${domain}/`,
    themeApi: `${protocol}://theme-api.${domain}/v1/theme`,
    exchangeJs: `${protocol}://exchange-js.${domain}/`,
    exchangeReact: `${protocol}://exchange-react.${domain}/`,
    exchangePhp: `${protocol}://exchange-php.${domain}/`,
  calculator: `${protocol}://calculator.${domain}/`,
  clock: `${protocol}://clock.${domain}/`,
  };
  return urls[service];
}

function SiteFooter(props) {
  const tokens = props.tokens;
  const links = props.links || {};
  const footerGroups = [
    {
      title: "Services",
      links: [
        links.theme && { href: links.theme, label: "Theme Lab" },
        links.time && { href: links.time, label: "Time API" },
        links.joke && { href: links.joke, label: "Joke API" },
        links.bitcoinApi && { href: links.bitcoinApi, label: "Bitcoin API" }
      ].filter(Boolean)
    },
    {
      title: "Community",
      links: [
        { href: "https://www.reddit.com/r/bitcoinagree/", label: "Reddit", external: true },
        { href: "https://x.com/bitcoinagree", label: "X (Twitter)", external: true },
        { href: "https://www.youtube.com/@bitcoinagree", label: "YouTube", external: true },
        { href: "https://github.com/bitcoinagree", label: "GitHub", external: true }
      ]
    },
    {
      title: "Info",
      links: [
        { href: "", label: "Privacy Policy" },
        { href: "", label: "Terms of Service" },
        { href: "", label: "Contact" }
      ]
    }
  ];
  const footerLinkStyle = {
    color: tokens.bitcoinStrong,
    display: "inline-flex",
    alignItems: "center",
    minHeight: 28,
    textDecorationColor: tokens.border,
    textUnderlineOffset: 3
  };

  return (
    <footer
      className="site-footer"
      style={{
        color: tokens.muted,
        borderTop: "1px solid " + tokens.border
      }}
    >
      <div className="site-footer-grid">
        {footerGroups.map((group) => (
          <div className="site-footer-group" key={group.title}>
            <div className="site-footer-title" style={{ color: tokens.text }}>{group.title}</div>
            <div className="site-footer-links">
              {group.links.map((item) => (
                <a
                  href={item.href}
                  key={item.label}
                  style={footerLinkStyle}
                  target={item.external ? "_blank" : undefined}
                  rel={item.external ? "noopener noreferrer" : undefined}
                >
                  {item.label}
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="site-footer-bottom">
        <div className="site-footer-nostr" style={{ color: tokens.bitcoinStrong }}>
          Find me on Nostr: bitcoinagree@bitcoinagree.com
        </div>

        <a
          href="https://primal.net/p/nprofile1qqsgquzttv6xac0ft4f7lspqsdfkg4dmalz2f5hpyc59c2yk5qv0xacrp8zm5"
          target="_blank"
          rel="noopener noreferrer"
          style={footerLinkStyle}
        >
          Primal
        </a>

        <div className="site-footer-made">
          Made with 🧡 by <span style={{ color: tokens.bitcoinStrong, fontWeight: 700 }}>BitcoinAgree</span>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  const [themeMode, setThemeMode] = useState(getStoredThemeMode());
  const [resolvedTheme, setResolvedTheme] = useState(resolveTheme(themeMode));
  const [themeConfig, setThemeConfig] = useState(DEFAULT_THEME_CONFIG);
  const [time, setTime] = useState("");
  const [timeCdt, setTimeCdt] = useState("");
  const [btcPrice, setBtcPrice] = useState(null);
  const [displayPrice, setDisplayPrice] = useState(null); // animated price
  const [animating, setAnimating] = useState(false);
  const [btcTimestamp, setBtcTimestamp] = useState(null);
  const [btcDateUtc, setBtcDateUtc] = useState("");
  const [btcDateCdt, setBtcDateCdt] = useState("");
  const [priceFormatted, setPriceFormatted] = useState("");

  useEffect(() => {
    function apply() {
      const nextTheme = resolveTheme(themeMode);
      setResolvedTheme(nextTheme);
      document.documentElement.setAttribute("data-theme", nextTheme);
      document.documentElement.setAttribute("data-theme-mode", themeMode);
      saveThemeMode(themeMode);
    }
    apply();
    let media;
    try {
      media = window.matchMedia("(prefers-color-scheme: dark)");
      if (themeMode === "auto") {
        if (media.addEventListener) media.addEventListener("change", apply);
        else if (media.addListener) media.addListener(apply);
      }
    } catch (e) {}
    return () => {
      if (media && themeMode === "auto") {
        if (media.removeEventListener) media.removeEventListener("change", apply);
        else if (media.removeListener) media.removeListener(apply);
      }
    };
  }, [themeMode]);

  useEffect(() => {
    function syncThemeMode(event) {
      if (event && event.type === "storage" && event.key !== THEME_KEY) return;
      const nextMode = getStoredThemeMode();
      setThemeMode(function(currentMode) {
        return currentMode === nextMode ? currentMode : nextMode;
      });
    }
    window.addEventListener("storage", syncThemeMode);
    window.addEventListener("focus", syncThemeMode);
    return function() {
      window.removeEventListener("storage", syncThemeMode);
      window.removeEventListener("focus", syncThemeMode);
    };
  }, []);

  useEffect(() => {
    document.title = APP_TITLE;

    function fetchTime() {
      fetch(getApiUrl("time"))
        .then((res) => res.text())
        .then((t) => {
          setTime(t);
          try {
            const timestamp = parseInt(t);
            const dtUtc = new Date(timestamp * 1000);
            setTime(dtUtc.toISOString().substr(11, 8));
            const dtCdt = new Date(dtUtc.getTime() - 5 * 60 * 60 * 1000);
            setTimeCdt(dtCdt.toISOString().substr(11, 8));
          } catch (e) {
            setTime(t);
            setTimeCdt("");
          }
        })
        .catch((e) => setTime("Error fetching time"));
    }

    function fetchPrice() {
      fetch(getApiUrl("bitcoinApi"))
        .then((res) => res.json())
        .then((data) => {
          if (data.timestamp && data.usd) {
            setBtcPrice(function(prev) {
              var newVal = data.usd;
              if (typeof prev === 'number' && prev !== newVal) {
                var start = prev;
                var end = newVal;
                var duration = 5000; // 5s
                var startTime = (typeof performance !== 'undefined' ? performance.now() : Date.now());
                setAnimating(true);
                function easeOutCubic(x) { return 1 - Math.pow(1 - x, 3); }
                function frame(ts) {
                  var now = ts || (typeof performance !== 'undefined' ? performance.now() : Date.now());
                  var elapsed = now - startTime;
                  var ratio = elapsed / duration;
                  if (ratio > 1) ratio = 1;
                  var eased = easeOutCubic(ratio);
                  var current = start + (end - start) * eased;
                  setDisplayPrice(current);
                  if (ratio < 1) {
                    requestAnimationFrame(frame);
                  } else {
                    setAnimating(false);
                    setDisplayPrice(end);
                  }
                }
                requestAnimationFrame(frame);
              } else {
                setDisplayPrice(newVal);
              }
              return newVal;
            });
            setBtcTimestamp(data.timestamp);
            const dtUtc = new Date(data.timestamp * 1000);
            setBtcDateUtc(dtUtc.toISOString().replace("T", " ").substring(0, 19) + " UTC");
            const dtCdt = new Date(dtUtc.getTime() - 5 * 60 * 60 * 1000);
            setBtcDateCdt(dtCdt.toISOString().replace("T", " ").substring(0, 19) + " CDT");
            // formatted price handled via displayPrice effect
          } else {
            setBtcPrice("Error: No bitcoin price data found");
            setPriceFormatted("Error: No bitcoin price data found");
          }
        })
        .catch((e) => {
          setBtcPrice("Error fetching bitcoin price");
          setPriceFormatted("Error fetching bitcoin price");
        });
    }

    // initial fetch
  fetchTime();
  fetchPrice();
    // interval (20s)
    const id = setInterval(() => {
      fetchTime();
      fetchPrice();
    }, 20000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(getApiUrl("themeApi"))
      .then((res) => {
        if (!res.ok) throw new Error("theme api " + res.status);
        return res.json();
      })
      .then((data) => {
        if (!cancelled && data && data.layout && data.themes) {
          setThemeConfig(data);
        }
      })
      .catch(() => {
        if (!cancelled) setThemeConfig(DEFAULT_THEME_CONFIG);
      });
    return () => { cancelled = true; };
  }, []);

  // Format animated price whenever it changes
  useEffect(() => {
    if (typeof displayPrice === 'number') {
      // Use integer formatting only (no decimal) to keep width stable and avoid shaking
      var intVal = Math.round(displayPrice);
      setPriceFormatted(intVal.toLocaleString(undefined));
    }
  }, [displayPrice]);

  const links = {
    time: getApiUrl("time"),
    joke: getApiUrl("joke"),
    bitcoinApi: getApiUrl("bitcoinApi"),
    bitcoinPrice: getApiUrl("bitcoinPrice"),
  bitcoinPriceDate: getApiUrl("bitcoinPriceDate"),
    theme: getApiUrl("theme"),
    exchangeJs: getApiUrl("exchangeJs"),
    exchangeReact: getApiUrl("exchangeReact"),
    exchangePhp: getApiUrl("exchangePhp"),
  calculator: getApiUrl("calculator"),
  clock: getApiUrl("clock"),
  themeApi: getApiUrl("themeApi"),
  };
  const tokens = getThemeTokens(resolvedTheme, themeConfig);
  const layout = themeConfig.layout || DEFAULT_THEME_CONFIG.layout;
  const modeButtons = ["auto", "light", "dark"];
  const domainName = (function() {
    try { return window.location.hostname || "bitcoinagree.com"; } catch (e) { return "bitcoinagree.com"; }
  })();
  const headerButtonStyle = {
    alignItems: "center",
    border: "1px solid " + tokens.bitcoin,
    borderRadius: layout.buttonRadius,
    background: tokens.surfaceSoft,
    boxSizing: "border-box",
    color: tokens.bitcoinStrong,
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 14,
    fontWeight: 600,
    height: 44,
    justifyContent: "center",
    padding: 0,
    width: 68
  };
  const activeHeaderButtonStyle = Object.assign({}, headerButtonStyle, {
    background: tokens.bitcoin,
    color: "#111827",
    fontWeight: 700
  });
  const shellStyle = {
    fontFamily: "Arial, sans-serif",
    minHeight: "100vh",
    background: tokens.bg,
    color: tokens.text,
    padding: layout.shellPadding
  };
  const innerStyle = {
    maxWidth: layout.maxWidth,
    margin: "0 auto"
  };
  const cardStyle = {
    background: tokens.surface,
    border: "1px solid " + tokens.border,
    borderRadius: layout.cardRadius,
    padding: layout.cardPadding,
    marginBottom: layout.cardMarginBottom,
    boxShadow: resolvedTheme === "dark" ? "none" : "0 2px 4px rgba(0,0,0,0.08)"
  };
  const softCardStyle = {
    background: tokens.surfaceSoft,
    border: "1px solid " + tokens.border,
    borderRadius: layout.cardRadius,
    padding: layout.softCardPadding
  };
  const cardLinkStyle = {
    textDecoration: "none",
    color: tokens.text,
    fontWeight: "bold",
    fontSize: "1.1em",
    display: "block"
  };
  const linkGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(" + layout.toolMinWidth + "px, 1fr))",
    gap: layout.gridGap,
    marginTop: layout.sectionGap
  };
  const utilityGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(" + layout.apiMinWidth + "px, 1fr))",
    gap: layout.gridGap
  };

  const isThemeHost = window.location.hostname === "theme.bitcoinagree.com" || window.location.hostname === "theme.test.local";
  if (isThemeHost || window.location.pathname === "/theme" || window.location.pathname === "/theme/") {
    return <ThemeLab tokens={tokens} layout={layout} themeConfig={themeConfig} themeMode={themeMode} setThemeMode={setThemeMode} resolvedTheme={resolvedTheme} links={links} />;
  }

  return (
    <div style={shellStyle}>
    <div style={innerStyle}>
      <header style={{display:"flex", justifyContent:"space-between", gap:16, alignItems:"center", flexWrap:"wrap", marginBottom:18}}>
        <div>
          <a href={links.bitcoinPrice.replace("price.", "")} style={{color:tokens.bitcoinStrong, textDecoration:"none", fontWeight:700}}>BitcoinAgree</a>
          <h1 style={{margin:"8px 0 0", fontSize:34}}>BitcoinAgree</h1>
          <div style={{fontSize:14, color:tokens.muted}}>{domainName}</div>
        </div>
        <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>
          {modeButtons.map(function(mode) {
            return (
              <button key={mode} type="button" style={themeMode === mode ? activeHeaderButtonStyle : headerButtonStyle} onClick={function(){ setThemeMode(mode); }}>
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            );
          })}
        </div>
      </header>

      <div style={cardStyle}>
        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", gap:16, flexWrap:"wrap"}}>
          <div style={{display:"flex", alignItems:"center", gap:12, minWidth:0}}>
            <img src="/bitcoin.svg" alt="Bitcoin" className={animating ? 'bitcoin-logo spin-once' : 'bitcoin-logo'} style={{ width: 38, height: 38, flex:"0 0 auto" }} />
            <div>
              <div style={{fontSize:13, color:tokens.muted}}>Live Bitcoin Price</div>
              <div style={{ fontSize: 30, fontWeight: 800, color: tokens.bitcoinStrong, letterSpacing: 0 }}>
                ${priceFormatted || "..."}
              </div>
            </div>
          </div>
          <a href={links.bitcoinPrice} style={{color:tokens.bitcoinStrong, textDecoration:"none", fontWeight:700}}>Open price display</a>
        </div>
      </div>

      <div style={cardStyle}>
        <h2 style={{marginTop:0, marginBottom:0}}>Tools</h2>
        <div style={linkGridStyle}>
          <a href={links.bitcoinPrice} style={Object.assign({}, cardLinkStyle, softCardStyle)}>
            📊 Bitcoin Fullscreen Price Display
          </a>
          <a href={links.bitcoinPriceDate} style={Object.assign({}, cardLinkStyle, softCardStyle)}>
            📅 Bitcoin Price on Date
          </a>
          <a href={links.calculator} style={Object.assign({}, cardLinkStyle, softCardStyle)}>
            🧮 Bitcoin Calculator
          </a>
          <a href={links.clock} style={Object.assign({}, cardLinkStyle, softCardStyle)}>
            ⏰ Bitcoin Clock
          </a>
          <a href={links.theme} style={Object.assign({}, cardLinkStyle, softCardStyle)}>
            🎨 Theme Lab
          </a>
        </div>
      </div>

      <div style={cardStyle}>
        <h2 style={{marginTop:0, marginBottom:0}}>APIs</h2>
        <div style={utilityGridStyle}>
          <a href={links.time} style={Object.assign({}, cardLinkStyle, softCardStyle, {fontSize:"1em"})}>Time API</a>
          <a href={links.joke} style={Object.assign({}, cardLinkStyle, softCardStyle, {fontSize:"1em"})}>Joke API</a>
          <a href={links.bitcoinApi} style={Object.assign({}, cardLinkStyle, softCardStyle, {fontSize:"1em"})}>Bitcoin API</a>
        </div>
      </div>

      <SiteFooter tokens={tokens} links={links} />
    </div>
    </div>
  );
}

function ThemeLab(props) {
  const tokens = props.tokens;
  const layout = props.layout || DEFAULT_THEME_CONFIG.layout;
  const themeConfig = props.themeConfig || DEFAULT_THEME_CONFIG;
  const themeMode = props.themeMode;
  const setThemeMode = props.setThemeMode;
  const resolvedTheme = props.resolvedTheme;
  const links = props.links || {};
  const domainName = (function() {
    try { return window.location.hostname || "theme.bitcoinagree.com"; } catch (e) { return "theme.bitcoinagree.com"; }
  })();
  const shellStyle = {fontFamily:"Arial, sans-serif", minHeight:"100vh", background:tokens.bg, color:tokens.text, padding:layout.shellPadding};
  const innerStyle = {maxWidth:layout.maxWidth, margin:"0 auto"};
  const cardStyle = {background:tokens.surface, border:"1px solid " + tokens.border, borderRadius:layout.cardRadius, padding:layout.cardPadding, marginBottom:layout.cardMarginBottom};
  const softCardStyle = {background:tokens.surfaceSoft, border:"1px solid " + tokens.border, borderRadius:layout.cardRadius, padding:layout.softCardPadding};
  const inputStyle = {width:"100%", boxSizing:"border-box", border:"1px solid " + tokens.border, borderRadius:layout.buttonRadius, background:tokens.surface, color:tokens.text, fontSize:17, padding:"9px 10px"};
  const buttonStyle = {border:"1px solid " + tokens.bitcoin, borderRadius:layout.buttonRadius, background:tokens.surfaceSoft, color:tokens.bitcoinStrong, cursor:"pointer", fontSize:14, fontWeight:600, padding:"9px 12px"};
  const activeButtonStyle = {border:"1px solid " + tokens.bitcoin, borderRadius:layout.buttonRadius, background:tokens.bitcoin, color:"#111827", cursor:"pointer", fontSize:14, fontWeight:700, padding:"9px 12px"};
  const modeButtonStyle = Object.assign({}, buttonStyle, {alignItems:"center", boxSizing:"border-box", display:"inline-flex", height:44, justifyContent:"center", padding:0, width:68});
  const activeModeButtonStyle = Object.assign({}, modeButtonStyle, {background:tokens.bitcoin, color:"#111827", fontWeight:700});
  const modeButtons = ["auto", "light", "dark"];

  return (
    <div style={shellStyle}>
      <div style={innerStyle}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", gap:16, flexWrap:"wrap", marginBottom:18}}>
          <div>
            <a href={getApiUrl("bitcoinPrice").replace("price.", "")} style={{color:tokens.bitcoinStrong, textDecoration:"none", fontWeight:700}}>BitcoinAgree</a>
            <h1 style={{margin:"8px 0 0"}}>Theme Lab</h1>
            <div style={{color:tokens.muted, fontSize:14}}>{domainName}</div>
          </div>
          <div style={{display:"flex", gap:8, flexWrap:"wrap"}}>
            {modeButtons.map(function(mode) {
              return (
                <button key={mode} type="button" style={themeMode === mode ? activeModeButtonStyle : modeButtonStyle} onClick={function(){ setThemeMode(mode); }}>
                  {mode.charAt(0).toUpperCase() + mode.slice(1)}
                </button>
              );
            })}
          </div>
        </div>

        <div style={cardStyle}>
          <h2 style={{marginTop:0}}>Shared Theme Setting</h2>
          <p style={{color:tokens.muted, marginTop:0}}>
            This page saves <code>{THEME_KEY}</code> for the selected mode. The visual contract comes from
            <code> theme-api </code>, so changing shared layout values there updates this lab and the home page together.
          </p>
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))", gap:12}}>
            <div style={softCardStyle}><strong>Auto</strong><br /><span style={{color:tokens.muted}}>Follows system theme.</span></div>
            <div style={softCardStyle}><strong>Light</strong><br /><span style={{color:tokens.muted}}>Forces light colors.</span></div>
            <div style={softCardStyle}><strong>Dark</strong><br /><span style={{color:tokens.muted}}>Forces dark colors.</span></div>
          </div>
        </div>

        <div style={cardStyle}>
          <h2 style={{marginTop:0}}>Shared Layout Contract</h2>
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))", gap:layout.gridGap}}>
            <div style={softCardStyle}><strong>Max Width</strong><br /><span style={{color:tokens.muted}}>{layout.maxWidth}px</span></div>
            <div style={softCardStyle}><strong>Card Padding</strong><br /><span style={{color:tokens.muted}}>{layout.cardPadding}px</span></div>
            <div style={softCardStyle}><strong>Card Radius</strong><br /><span style={{color:tokens.muted}}>{layout.cardRadius}px</span></div>
            <div style={softCardStyle}><strong>Grid Gap</strong><br /><span style={{color:tokens.muted}}>{layout.gridGap}px</span></div>
          </div>
          {links.themeApi && <p style={{color:tokens.muted, marginBottom:0}}>Source: <a href={links.themeApi} style={{color:tokens.bitcoinStrong}}>theme-api JSON</a></p>}
        </div>

        <div style={cardStyle}>
          <h2 style={{marginTop:0}}>Elements</h2>
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(" + layout.toolMinWidth + "px, 1fr))", gap:layout.gridGap}}>
            <label style={{display:"flex", flexDirection:"column", gap:6}}>
              Input
              <input style={inputStyle} defaultValue="0.01 BTC" />
            </label>
            <label style={{display:"flex", flexDirection:"column", gap:6}}>
              Select
              <select style={inputStyle} defaultValue="auto">
                <option value="auto">Auto</option>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </label>
          </div>
          <div style={{display:"flex", gap:10, flexWrap:"wrap", marginTop:layout.sectionGap}}>
            <button style={activeButtonStyle}>Primary</button>
            <button style={buttonStyle}>Secondary</button>
            <button style={Object.assign({}, buttonStyle, {borderColor:tokens.success, color:tokens.success})}>Success</button>
            <button style={Object.assign({}, buttonStyle, {borderColor:tokens.danger, color:tokens.danger})}>Danger</button>
          </div>
        </div>

        <div style={cardStyle}>
          <h2 style={{marginTop:0}}>Sample Tool Card</h2>
          <div style={{display:"flex", gap:layout.gridGap, flexWrap:"wrap"}}>
            <div style={Object.assign({}, softCardStyle, {flex:"1 1 220px"})}>
              <div style={{fontSize:13, color:tokens.muted}}>Bitcoin Price</div>
              <div style={{fontSize:28, fontWeight:800, color:tokens.bitcoinStrong}}>$77,000</div>
            </div>
            <div style={Object.assign({}, softCardStyle, {flex:"1 1 220px"})}>
              <div style={{fontSize:13, color:tokens.muted}}>0.01 BTC</div>
              <div style={{fontSize:28, fontWeight:800}}>$770.00</div>
            </div>
          </div>
        </div>

        <SiteFooter tokens={tokens} links={links} />
      </div>
    </div>
  );
}



// WEBPACK FOOTER //
// ./src/App.js